<template>
    <div class="traceability-contract-create-page">
        <base-header :titulo="translate('messages.create_title')" :voltar-para="{ route: 'contrato-rastreabilidade' }" />
        <div class="view-container-wrapper">
            <el-form label-position="top">
                <div class="content">
                    <contract-data ref="contractData" />
                    <protocol-select ref="protocolSelect" />
                    <property-select ref="propertySelect" />
                    <contracted-volume-progress-bar ref="volumeProgressBar" />
                    <farm-list ref="farmList" :selected.sync="selected" />
                </div>
            </el-form>
            <div class="actions">
                <div class="actions__link">
                    <el-link
                        :disabled="selected.length === 0"
                        type="danger"
                        :underline="false"
                        @click.native="handleRemoveSelected()"
                    >
                        {{ translate('words.delete_selected_farms') }} ({{
                            selected.length
                        }})
                    </el-link>
                </div>
                <el-button @click="draft" :loading="loading">
                    {{ translate('words.save') }}
                </el-button>
                <el-button
                    type="primary"
                    :disabled="!isFormValid"
                    @click="validateVolume"
                    :loading="loading"
                >
                    {{ translate('words.finish') }}
                </el-button>
            </div>
        </div>
        <dialog-contract-finish
            v-if="showDialogContractFinish"
            v-model="showDialogContractFinish"
            @onSent="sent"
        />
    </div>
</template>

<script>
import { mapActions, mapState } from 'vuex';

export default {
    components: {
        BaseHeader: () => import('@/components/BaseHeader'),
        FarmList: () =>
            import(
                '@/modules/traceability-contract/views/components/farm-list/index.vue'
            ),
        ContractedVolumeProgressBar: () =>
            import(
                '@/modules/traceability-contract/views/components/contracted-volume-progress-bar/index.vue'
            ),
        ContractData: () =>
            import(
                '@/modules/traceability-contract/views/components/contract-data/index.vue'
            ),
        ProtocolSelect: () =>
            import(
                '@/modules/traceability-contract/views/components/protocol-select/index.vue'
            ),
        PropertySelect: () =>
            import(
                '@/modules/traceability-contract/views/components/property-select/index.vue'
            ),
        dialogContractFinish: () => import('@/modules/traceability-contract/views/components/dialog-contract-finish/index.vue')
    },
    data() {
        return {
            selected: [],
            loading: false,
            showDialogContractFinish: false
        };
    },
    beforeDestroy() {
        this.$store.commit('traceabilityContractModule/RESET_FORM');
    },
    computed: {
        ...mapState('traceabilityContractModule', ['form', 'files']),
        isFormValid() {
            return this.contractDataValidate();
        }
    },
    methods: {
        ...mapActions('traceabilityContractModule', [
            'createTraceabilityReport',
            'updateTraceabilityReport',
            'persistDetailFiles',
            'fetchAvailableBalance',
            'validateAvailableBalance',
            'detail'
        ]),
        async draft() {
            await this.$messageBox.confirm(
                this.translate('messages.dialog_save_contract.message'),
                this.$t('contrato_rastreabilidade.words.warning'),
                {
                    confirmButtonText: this.translate('words.yes'),
                    cancelButtonText: this.translate('words.cancel'),
                    type: 'warning'
                }
            ).then(async () => {
                try {
                    this.loading = true;
                    this.form.status = 'NAO_ENVIADO';
                    if (this.form.id) {
                        const { data } = await this.updateTraceabilityReport(this.form);

                        await this.saveFiles(data);
                    } else {
                        const { data } = await this.createTraceabilityReport(this.form);
                        
                        await this.saveFiles(data);
                    }
                    await this.$router.push({
                        name: 'contrato-rastreabilidade'
                    });
                    this.$notify.success(this.translate('messages.dialog_save_contract.saved_successfully'));
                } catch (error) {
                    this.$notify.error(this.translate('messages.dialog_save_contract.saved_error'));
                } finally {
                    this.loading = false;
                }
            }).catch(() => {});
        },
        async openSend() {
            await this.$messageBox.confirm(
                this.translate('messages.dialog_send_contract.message'),
                'Aviso',
                {
                    confirmButtonText: this.translate('words.yes'),
                    cancelButtonText: this.translate('words.cancel'),
                    type: 'warning'
                }
            ).then(async () => {
                this.showDialogContractFinish = true;
            }).catch(() => {});
        },
        sent() {
            this.$router.push({ name: 'contrato-rastreabilidade' });
        },
        contractDataValidate() {
            return !!(
                this.form.contract_data.contract_number &&
                this.form.contract_data.volume &&
                this.form.contract_data.cultivation_id &&
                this.form.contract_data.department &&
                this.form.contract_data.district &&
                this.form.contract_data.client_id &&
                this.form.contract_data.contract_date
            );
        },
        translate(key, params = {}) {
            const base = 'contrato_rastreabilidade';
            return this.$t(`${base}.${key}`, params);
        },
        async handleRemoveSelected() {
            const count = this.selected.length;
            if (!count) {
                return;
            }

            await this.$messageBox
                .confirm(
                    this.$tc(
                        'contrato_rastreabilidade.messages.modal_delete_selected_farm',
                        count
                    ),
                    this.$tc(
                        'contrato_rastreabilidade.messages.modal_delete_selected_farm_title',
                        count
                    ),
                    {
                        confirmButtonText: this.translate('words.confirm'),
                        cancelButtonText: this.translate('words.cancel')
                    }
                )
                .then(() => {
                    this.selected.forEach(selected => {
                        const index = this.form.properties.findIndex(
                            farm => farm.id === selected.id
                        );
                        if (index > -1) {
                            this.form.properties.splice(index, 1);
                        }
                    });
                })
                .catch(() => {});
        },
        validateVolume() {
            const farms = this.form.properties;

            const totalContractsVolume = farms
                .map(f => Number(f.volume) || 0)
                .reduce((a, b) => a + b, 0);

            const reportVolume = Number(this.form.contract_data.volume || 0);

            if (totalContractsVolume === 0) {
                this.$notify.error({
                    message: this.translate('messages.dialog_send_contract.emptyFarms')
                });
                return;
            }

            if (totalContractsVolume > 0 && totalContractsVolume < reportVolume) {
                this.$notify.error({
                    message: this.translate('messages.dialog_send_contract.contractsVolumeLessThanReport')
                });
                return;
            }

            this.openSend();
        },
        async saveFiles(data) {
            const form = await this.detail(data);
            this.form.properties.forEach((p1) => {
                const pLyraCodeIndex = form.properties.findIndex(propertie => {
                    return propertie.territory_lyra_code === p1.territory_lyra_code;
                });

                if (pLyraCodeIndex >= 0) {
                    form.properties[pLyraCodeIndex].invoice_attachments = p1.invoice_attachments;
                }
            });
            
            await this.persistDetailFiles(form);
        }
    }
};
</script>

<style scoped lang="scss">
.traceability-contract-create-page {
    .view-container-wrapper {
        display: flex;
        flex-direction: column;
        justify-content: space-between;
    }

    .content {
        display: flex;
        flex-direction: column;
        gap: 16px;
        padding: 16px;
    }

    .actions {
        padding: 0 16px;
        margin: 16px 0;
        display: grid;
        grid-template-columns: 4fr auto auto;
        gap: 15px;

        .actions__link {
            display: flex;
            justify-content: flex-start;
            align-items: center;

            .el-link {
                color: #dc362eff;
                padding: 4px;
            }
        }

        .el-button {
            margin: 0;
            width: 100px;
        }

        @media (max-width: 768px) {
            grid-template-columns: 1fr;
            .el-button {
                margin: 0;
                width: 100%;
            }
        }
    }
}
</style>
